// assets/AllCourses/Exercises/VocabularyBuildingExercise.jsx

import React, { useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField } from "@/components/shared/FormFields";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function VocabularyBuildingExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= NORMALIZE DATA ================= */
  const questions = Array.isArray(exercise.vocabularyQuestions)
    ? exercise.vocabularyQuestions.map((q) => ({
        ...q,
        answer: q.correctAnswer,
        type: "single",
      }))
    : [];

  /* ================= SAFE ANSWERS ================= */
  const safeExternalAnswers =
    typeof userAnswer === "object" ? userAnswer : {};

  /* ================= ENGINE ================= */
  const { answers, setAnswer, getOptionState } = useExerciseEngine({
    questions,
    showResult: showResults,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC TO PARENT ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;
  if (!Object.keys(answers).length) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers]);

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    const updateQuestions = (next) =>
      onUpdate("vocabularyQuestions", next);

    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Word ${qIdx + 1}`}
              value={q.word || ""}
              onChange={(v) => {
                const next = [...questions];
                next[qIdx].word = v;
                updateQuestions(next);
              }}
            />

            {/* OPTIONS */}
            <div className="space-y-3">
              {q.options?.map((opt, optIdx) => (
                <div key={optIdx} className="flex gap-3">
                  <StringField
                    value={opt}
                    onChange={(v) => {
                      const next = [...questions];
                      next[qIdx].options[optIdx] = v;
                      updateQuestions(next);
                    }}
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      const next = [...questions];
                      next[qIdx].options = q.options.filter(
                        (_, i) => i !== optIdx
                      );
                      updateQuestions(next);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}

              <Button
                size="sm"
                onClick={() => {
                  const next = [...questions];
                  next[qIdx].options.push("");
                  updateQuestions(next);
                }}
              >
                Add Option
              </Button>
            </div>

            <StringField
              label="Correct Answer"
              value={q.correctAnswer || ""}
              onChange={(v) => {
                const next = [...questions];
                next[qIdx].correctAnswer = v;
                updateQuestions(next);
              }}
            />

            <Button
              size="sm"
              variant="destructive"
              onClick={() =>
                updateQuestions(
                  questions.filter((_, i) => i !== qIdx)
                )
              }
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                word: "",
                options: [],
                correctAnswer: "",
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No vocabulary questions available
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Vocabulary Builder</CardTitle>
        <CardDescription>
          Choose the correct meaning
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const selected = answers[qIdx] ?? "";

          return (
            <div key={qIdx} className="space-y-6">
              {/* WORD */}
              <div className="text-3xl font-bold text-center">
                {q.word}
              </div>

              <RadioGroup
                value={selected}
                onValueChange={(v) => {
                  setAnswer(qIdx, v);
                }}
                disabled={showResults}
                className="space-y-3"
              >
                {q.options.map((opt, optIdx) => {
                  const state = getOptionState(
                    selected,
                    opt,
                    q.correctAnswer
                  );

                  return (
                    <label
                      key={optIdx}
                      htmlFor={`vocab-${qIdx}-${optIdx}`}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border p-4 transition cursor-pointer hover:bg-muted/60 active:scale-[0.98]",
                        state.className
                      )}
                    >
                      <RadioGroupItem
                        value={opt}
                        id={`vocab-${qIdx}-${optIdx}`}
                      />
                      <span className="flex-1">{opt}</span>
                    </label>
                  );
                })}
              </RadioGroup>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}